import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Libertin } from '../interfaces/libertin'
import { User } from '../interfaces/user'
import { fetchUsers } from '../../services/fetchAllUser'
import { fetchAnnounceDetails } from '../../services/announcesLibertins'
import calculateAge from '../fonctions/calculateAge'
import { calculateDistance } from '../fonctions/calculateDistance'
import citiesData from '../../src/data/cities.json'
import { useSearchFilters } from './SearchFiltersContext'
import ProfileCard from './ProfileCard'
import { useTranslation } from 'react-i18next'
import { NoAnnouncesFound } from './NoAnnouncesFound'
import { ActiveFilters } from './ActiveFilters'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import LibertinCard from './LibertinCard'

const ITEMS_PER_PAGE = 12

const distanceRanges: { [key: string]: number[] } = {
  '< 10km': [0, 10],
  '< 20km': [0, 20],
  '< 50km': [0, 50],
  '< 75km': [0, 75],
  '< 100km': [0, 100],
}

const getCityCoordinates = (cityName: string) => {
  for (const province of citiesData) {
    const city = province.cities.find(c => c.city_fr === cityName)
    if (city) {
      return { latitude: city.latitude, longitude: city.longitude }
    }
  }
  return { latitude: 0, longitude: 0 }
}

const defaultImageUrl = (gender: string | undefined) => {
  const baseUrl = window.location.origin

  switch (gender) {
    case 'man':
      return `${baseUrl}/images/silouhette-male.jpg`
    case 'woman':
      return `${baseUrl}/images/silouhette-female.jpg`
    case 'transgender':
      return `${baseUrl}/images/silouhette-transgender.jpg`
    case 'couple':
      return `${baseUrl}/images/silouhette-couple.jpg`
    default:
      return `${baseUrl}/images/silouhette.jpg`
  }
}

const LibertinsHome: React.FC = () => {
  const { t } = useTranslation()
  const [libertins, setLibertins] = useState<Libertin[]>([])
  const [filteredLibertins, setFilteredLibertins] = useState<Libertin[]>([])
  const [displayedLibertins, setDisplayedLibertins] = useState<Libertin[]>([])
  const [users, setUsers] = useState<User[]>([])
  const { filters, setFilters } = useSearchFilters()
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [statusToShow, setStatusToShow] = useState([1])

  const observer = useRef<IntersectionObserver>()
  const lastLibertinElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          setPage(prevPage => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [loading, hasMore],
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersData = await fetchUsers()
        setUsers(usersData)

        const response = await fetch(`${API_BASE_URL}/libertins`, {
          headers: {
            'x-api-key': X_API_KEY || '',
          },
        })
        if (!response.ok) {
          throw new Error('Failed to fetch libertins')
        }
        const data = await response.json()
        const libertinIds = data.map((libertin: { id: string }) => libertin.id)

        const libertinDetails = await Promise.all(
          libertinIds.map(async (id: string) => {
            const libertinData = await fetchAnnounceDetails(id)
            const user = usersData.find(
              user => user.id === libertinData.libertin.userId,
            )

            const filteredGallery = (libertinData.gallery || []).filter(
              (img: any) =>
                img.status.toString().includes(statusToShow.toString()),
            )

            return {
              ...libertinData.libertin,
              age: user ? calculateAge(user.birthday) : null,
              user: user || {
                id: 0,
                birthday: '',
                nickname: '',
                userProfile: { gender: '', orientation: '' },
                address: { city: '' },
              },
              galleryUrl:
                filteredGallery.find((img: any) => img.cover)?.fileUrl ||
                filteredGallery[0]?.fileUrl ||
                defaultImageUrl(user?.userProfile?.gender),
              gallery: filteredGallery,
            }
          }),
        )

        const sortedLibertins = libertinDetails.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )

        setLibertins(sortedLibertins)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [statusToShow])

  useEffect(() => {
    const filtered = libertins.filter(libertin => {
      const user = users.find(u => u.id === libertin.user.id)
      if (!user || user.isBanned) return false

      const matchesJeCherche =
        filters.imLookingFor.length === 0 ||
        (filters.imLookingFor.length > 0 &&
          filters.imLookingFor.includes(user.userProfile.gender))

      const matchesOrientation =
        filters.orientation.length === 0 ||
        (filters.orientation.length > 0 &&
          filters.orientation.includes(user.userProfile.orientation))

      let matchesLocation = filters.iLiveIn.length === 0

      if (!matchesLocation && filters.iLiveIn.length > 0) {
        const filterCity = getCityCoordinates(filters.iLiveIn[0])
        const userCity = getCityCoordinates(user.address.city)

        if (filterCity && userCity) {
          if (filters.distance.length === 0) {
            matchesLocation = user.address.city === filters.iLiveIn[0]
          } else {
            const distance = calculateDistance(
              filterCity.latitude,
              filterCity.longitude,
              userCity.latitude,
              userCity.longitude,
            )

            const [, maxDistance] = distanceRanges[filters.distance[0]] || [
              0,
              Infinity,
            ]
            matchesLocation = distance <= maxDistance
          }
        }
      }

      return matchesJeCherche && matchesOrientation && matchesLocation
    })

    if (filters.iLiveIn.length > 0 && filters.distance.length > 0) {
      const referenceCity = getCityCoordinates(filters.iLiveIn[0])
      if (referenceCity) {
        filtered.sort((a, b) => {
          const cityA = getCityCoordinates(a.user.address.city)
          const cityB = getCityCoordinates(b.user.address.city)
          if (cityA && cityB) {
            const distanceA = calculateDistance(
              referenceCity.latitude,
              referenceCity.longitude,
              cityA.latitude,
              cityA.longitude,
            )
            const distanceB = calculateDistance(
              referenceCity.latitude,
              referenceCity.longitude,
              cityB.latitude,
              cityB.longitude,
            )
            return distanceA - distanceB
          }
          return 0
        })
      }
    }

    setFilteredLibertins(filtered)
    setPage(1)
    setDisplayedLibertins([])
  }, [filters, users, libertins])

  useEffect(() => {
    setLoading(true)
    const startIndex = (page - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const newLibertins = filteredLibertins.slice(startIndex, endIndex)

    setDisplayedLibertins(prev => [
      ...prev,
      ...newLibertins.filter(
        newLibertin => !prev.some(libertin => libertin.id === newLibertin.id),
      ),
    ])
    setHasMore(endIndex < filteredLibertins.length)
    setLoading(false)
  }, [page, filteredLibertins])

  useEffect(() => {
    const savedFilters = localStorage.getItem('searchFilters')
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const cleanedFilters = {
          imLookingFor:
            parsedFilters.imLookingFor?.filter((item: string) => item !== '') ||
            [],
          orientation:
            parsedFilters.orientation?.filter((item: string) => item !== '') ||
            [],
          iLiveIn:
            parsedFilters.iLiveIn?.filter((item: string) => item !== '') || [],
          distance:
            parsedFilters.distance?.filter((item: string) => item !== '') || [],
        }
        setFilters(cleanedFilters)
      } catch (error) {
        console.error('Error parsing saved filters:', error)
      }
    }
  }, [setFilters])

  const handleRemoveFilter = (type: string) => {
    const newFilters = { ...filters }

    switch (type) {
      case 'location':
        newFilters.iLiveIn = []
        newFilters.distance = []
        break
      case 'looking':
        newFilters.imLookingFor = []
        break
      case 'orientation':
        newFilters.orientation = []
        break
      case 'distance':
        newFilters.distance = []
        break
    }

    setFilters(newFilters)
    localStorage.setItem('searchFilters', JSON.stringify(newFilters))
  }

  return (
    <>
      <ActiveFilters filters={filters} onRemoveFilter={handleRemoveFilter} />
      <div className='px-2 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4 md:gap-2'>
        {libertins.length === 0 ? (
          <div className='col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-6'>
            <NoAnnouncesFound
              hasActiveFilters={Object.values(filters).some(
                filterArray => filterArray.length > 0,
              )}
            />
          </div>
        ) : displayedLibertins.length > 0 ? (
          displayedLibertins.map((libertin, index) => (
            <div
              key={libertin.id}
              ref={
                index === displayedLibertins.length - 1
                  ? lastLibertinElementRef
                  : null
              }
            >
              <LibertinCard
                libertin={libertin}
                user={libertin.user}
                statusToShow={statusToShow}
                gallery={libertin.gallery || []}
              />
            </div>
          ))
        ) : (
          <div className='col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-4 xl:col-span-6'>
            <NoAnnouncesFound
              hasActiveFilters={Object.values(filters).some(
                filterArray => filterArray.length > 0,
              )}
            />
          </div>
        )}
        {loading && (
          <div className='col-span-2 text-center'>{t('page-loading')}</div>
        )}
      </div>
    </>
  )
}

export default LibertinsHome
